import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { MoveRight, Folder, Info } from 'lucide-react';

import BaseModal from '@/shared/components/modals/core/BaseModal.jsx';
import Button from '@/shared/components/elements/Button';

function MoveDashboardModal({
  isOpen,
  onClose,
  dashboard,
  folders = [],
  onConfirm,
  isLoading = false,
}) {
  const [selectedFolderId, setSelectedFolderId] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setSelectedFolderId(null);
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedFolderId) {
      onConfirm(selectedFolderId);
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="انتقال داشبورد"
      subtitle={dashboard?.name}
      icon={<MoveRight size={20} />}
      iconVariant="info"
      size="md"
      closeOnBackdropClick={!isLoading}
      closeOnEscape={!isLoading}
      showCloseButton={!isLoading}
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            fullWidth
            onClick={onClose}
            disabled={isLoading}
          >
            انصراف
          </Button>
          <Button
            type="button"
            variant="primary"
            size="lg"
            fullWidth
            loading={isLoading}
            disabled={!selectedFolderId || isLoading}
            icon={<MoveRight size={18} />}
            iconPosition="left"
            onClick={handleConfirm}
          >
            انتقال
          </Button>
        </>
      }
    >
      <div className="space-y-5 p-6">
        <div>
          <label className="mb-3 block text-sm font-medium">
            انتخاب فولدر مقصد
          </label>
          <div className="max-h-64 space-y-2 overflow-y-auto">
            {folders.length === 0 ? (
              <div className="py-8 text-center text-sm text-gray-400 dark:text-gray-500">
                فولدر دیگری برای انتقال وجود ندارد
              </div>
            ) : (
              folders.map((folder) => {
                const isSelected = selectedFolderId === folder.id;

                return (
                  <button
                    key={folder.id}
                    type="button"
                    onClick={() => setSelectedFolderId(folder.id)}
                    disabled={isLoading}
                    className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 text-right transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
                      isSelected
                        ? 'border-blue-500 bg-blue-500 text-white'
                        : 'border-gray-200 bg-gray-50 text-gray-700 hover:border-blue-400 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:border-blue-400'
                    }`}
                  >
                    <Folder size={18} />
                    <span className="font-medium">{folder.name}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>

        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 backdrop-blur-sm dark:border-blue-500/30 dark:bg-blue-500/10">
          <div className="flex items-start gap-2">
            <Info
              size={16}
              className="mt-0.5 text-blue-600 dark:text-blue-400"
            />
            <p className="text-xs leading-relaxed text-blue-700 dark:text-blue-300">
              داشبورد به فولدر انتخابی منتقل خواهد شد و در لیست فولدر فعلی نمایش
              داده نخواهد شد.
            </p>
          </div>
        </div>
      </div>
    </BaseModal>
  );
}

MoveDashboardModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dashboard: PropTypes.object,
  folders: PropTypes.array,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
};

export default MoveDashboardModal;
